import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import React, { useRef, useEffect } from "react";
gsap.registerPlugin(ScrollTrigger);

/* eslint-disable-next-line */
export interface FromToEffectProps {
  scrollTrigger?: ScrollTrigger.Vars;
  from?: GSAPTweenVars;
  to?: GSAPTweenVars;
  set?: GSAPTweenVars;
  children?: React.ReactNode;
  wrap?: boolean;
  parallax?: boolean;
  deps?:any[]
}

interface CustomObjectWithDOMNode {
  getDOMNode: () => HTMLElement | null;
}

type ReactElementWithRef = React.ReactElement & {
  ref?: React.Ref<any>;
};

// 类型判断辅助函数

function isHTMLElement(obj: any): obj is HTMLElement {
  return obj instanceof HTMLElement;
}

function isReactComponent(obj: any): obj is React.Component {
  return obj instanceof React.Component;
}

function isCustomObject(obj: any): obj is CustomObjectWithDOMNode {
  return typeof obj === "object" && "getDOMNode" in obj;
}

export const FromToEffect = (props: FromToEffectProps) => {
  const {
    scrollTrigger,
    from,
    to,
    set,
    children,
    wrap = true,
    parallax = false,
    deps = []
  } = props;
  const wrapRef = useRef<HTMLDivElement>(null);
  const targetRefs = useRef<(HTMLElement | null)[]>([]);
  const childrenWithRefs = React.Children.map(
    children as ReactElementWithRef[],
    (child, index) => {
      if (!React.isValidElement(child)) {
        return child;
      }

      // 获取原始的 ref
      const originalRef = child.ref;

      const childRef = (
        el: HTMLElement | React.Component | CustomObjectWithDOMNode | null
      ) => {
        // 判断el是HTMLElement（对于子级是 HTML 节点或者是使用 forwardRef 创建的函数组件，且在子组件内有将从父级传递进去的 ref 附加的 DOM 节点上，这样子组件内部无法使用自身的 ref 进行其他操作，一般不推荐这种做法）
        if (isHTMLElement(el)) {
          targetRefs.current[index] = el;
        }
        // 判断el是React组件并且具有getDOMNode方法（对于类组件，仅需要类组件暴露一个 getDOMNode 方法，返回子组件的最外层 DOM 节点，推荐这个做法，因为不会影响子组件的其他功能）
        else if (el !== null && isReactComponent(el) && isCustomObject(el)) {
          targetRefs.current[index] = el.getDOMNode();
        }
        // 判断el是具有getDOMNode方法的自定义对象（对于函数组件，仅需要使用 forwardRef 和 useImperativeHandle 创建，暴露出一个 getDOMNode 方法，返回子组件的最外层 DOM 节点，推荐这个做法，因为不会影响子组件的其他功能）
        else if (el !== null && isCustomObject(el) && isCustomObject(el)) {
          targetRefs.current[index] = el.getDOMNode();
        } else {
          // 处理null或其他情况
          targetRefs.current[index] = null;
        }

        // 将新的 DOM 元素分配给原始 ref，使得下面的 return React.cloneElement(child, { ...child.props, ref: childRef }); 不会将日常使用过程中操作子组件内部的方法时传递的 ref 覆盖
        if (originalRef) {
          if (typeof originalRef === "function") {
            originalRef(el);
          } else {
            (originalRef as React.MutableRefObject<any>).current = el;
          }
        }
      };
      return React.cloneElement(child, { ...child.props, ref: childRef });
    }
  );

  useEffect(() => {
    let elements: NodeListOf<HTMLElement> | (HTMLElement | null)[];
    // 处理有包裹 div 的情况
    if (wrap && wrapRef.current) {
      elements = wrapRef.current.querySelectorAll(":scope > *");
      const ctx = gsap.context(() => {
        
        elements.forEach((element: HTMLElement | null) => {
          if (element) {
            gsap.set(element, set ? set : {});
            gsap.fromTo(
              element,
              from ? from : {},
              to
                ? {
                    scrollTrigger: {
                      trigger: element,
                      start: "top bottom",
                      ...scrollTrigger,
                    },
                    y:
                      parallax && element.parentElement
                        ? - (element.offsetHeight -
                          element.parentElement.offsetHeight)
                        : to.y,
                    ...to,
                  }
                : {
                    scrollTrigger: {
                      trigger: element,
                      start: "top bottom",
                      ...scrollTrigger,
                    },
                    y:
                      parallax && element.parentElement
                        ? - (element.offsetHeight -
                          element.parentElement.offsetHeight)
                        : undefined,
                  }
            );
          }
        });
      });

      return () => ctx.revert();

      // 处理无包裹 div 的情况
    } else if (!wrap && targetRefs.current) {
      elements = targetRefs.current;
      const ctx = gsap.context(() => {
        elements.forEach((element: HTMLElement | null, index) => {
          if (element) {
            gsap.set(element, set ? set : {});
            gsap.fromTo(
              element,
              from ? from : {},
              to
                ? {
                    scrollTrigger: {
                      trigger: element,
                      start: "top bottom",
                      ...scrollTrigger,
                    },
                    y:
                      parallax && element.parentElement
                        ? - (element.offsetHeight -
                          element.parentElement.offsetHeight)
                        : to.y,
                    ...to,
                  }
                : {
                    scrollTrigger: {
                      trigger: element,
                      start: "top bottom",
                      ...scrollTrigger,
                    },
                    y:
                      parallax && element.parentElement
                        ? - (element.offsetHeight -
                          element.parentElement.offsetHeight)
                        : undefined,
                  }
            );
          }
        });
      });

      return () => ctx.revert();
    }
  }, [...deps]);
  return wrap ? <div ref={wrapRef}>{children}</div> : <>{childrenWithRefs}</>;
};

export default FromToEffect;
